import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { Flex, Stack, Text, Tooltip } from '@chakra-ui/react';

import { useAuthStore } from '@/client/services/state/authStore';
import { useLayoutEffect } from 'react';
import {
  useHubSessionStore,
  useIsActiveHubSession,
} from '@/client/services/state/hubSessionStore';
import { useTranslation } from 'react-i18next';

export const HUB_SESSION_BANNER_HEIGHT = '56px';

/**
 * Root CSS variable carrying the banner's current height (0px when no hub
 * session). Any viewport-height layout — `calc(100vh - <chrome>)` panels
 * with bottom-pinned actions, full-height editors — must subtract it:
 *   height={`calc(100vh - 80px - var(${HUB_SESSION_BANNER_CSS_VAR}, 0px))`}
 * Otherwise the banner pushes the layout down and its bottom edge (usually
 * the action buttons) lands below the fold.
 */
export const HUB_SESSION_BANNER_CSS_VAR = '--hub-session-banner-height';

/**
 * Persistent "you are acting in another org" bar shown for the whole hub
 * mirror session, above MainNav (which shifts down by
 * HUB_SESSION_BANNER_HEIGHT while this renders — see LoggedInLayout/SideNav).
 * Everything shown is server-verified off the auth payload: child company,
 * user name, and (since S13/A4) the parent org's display name via
 * `hub_parent_company_name` — never a spoofable opener query param.
 */
export default function HubSessionBanner() {
  const { t } = useTranslation();
  const { authConfig, logout } = useAuthStore();
  const { clearHubSession } = useHubSessionStore();
  const isActiveHubSession = useIsActiveHubSession();

  // Layout effect: the var must be set BEFORE first paint — a reload into
  // an active hub session would otherwise paint one frame of every
  // calc(100vh…) consumer 56px too tall (the exact cutoff this var fixes).
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      HUB_SESSION_BANNER_CSS_VAR,
      isActiveHubSession ? HUB_SESSION_BANNER_HEIGHT : '0px',
    );
  }, [isActiveHubSession]);

  if (!isActiveHubSession) return null;

  const exitSession = () => {
    clearHubSession();
    // Full session teardown; logout() redirects to /login as the fallback for
    // when window.close() is refused (tab not script-opened).
    logout();
    window.close();
  };

  const parentCompanyName = authConfig.user?.hub_parent_company_name;
  const actingInLabel = parentCompanyName
    ? t('hubs.banner.actingInWithParent', {
        childCompanyName: authConfig.company?.name,
        parentCompanyName,
      })
    : t('hubs.banner.actingIn', {
        childCompanyName: authConfig.company?.name,
      });

  return (
    <Flex
      position="fixed"
      top={0}
      left={0}
      right={0}
      height={HUB_SESSION_BANNER_HEIGHT}
      zIndex={1100}
      backgroundColor="orange.100"
      borderBottom="1px"
      borderBottomColor="orange.300"
      paddingX={6}
      alignItems="center"
      justifyContent="space-between"
      gap={4}
      data-testid="hub-session-banner"
    >
      <Stack spacing={0} minWidth={0}>
        {/* Fixed banner height: truncate + tooltip rather than overflow on
            narrow screens (the nav offsets are keyed to this exact height) */}
        <Tooltip label={actingInLabel}>
          <Text noOfLines={1} fontSize="sm" fontWeight={700} color="orange.900">
            {actingInLabel}
          </Text>
        </Tooltip>
        <Text noOfLines={1} fontSize="xs" color="orange.800">
          {t('hubs.banner.signedInAs', { userName: authConfig.user?.full_name })}
        </Text>
      </Stack>

      <SolidButton size="sm" flexShrink={0} onClick={exitSession}>
        {t('hubs.banner.exit')}
      </SolidButton>
    </Flex>
  );
}
